'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RoomEvent, Track } from 'livekit-client';
import { Mic } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { BarVisualizer, useLocalParticipant, useRemoteParticipants, useRoomContext, useTracks, useVoiceAssistant, } from '@livekit/components-react';
import { MicrophoneIcon, SpeakerSimpleHighIcon, SpeakerSlashIcon, } from '@phosphor-icons/react';
import { MicrophoneSlashIcon } from '@phosphor-icons/react/dist/ssr';
import { useChatOnly } from '@/app/context/useChatOnly';
import { useMicPermission } from '@/app/context/useMicrophone';
import { useModal } from '@/app/context/useModal';
import { useMode } from '@/app/context/useMode';
import { ActionBar } from '@/components/embed-popup/action-bar';
import { AudioVisualizer } from '@/components/embed-popup/audio-visualizer';
import { Transcript } from '@/components/embed-popup/transcript';
import { getAssetUrl } from '@/helpers/commonFunctions';
import { useAgentControlBar } from '@/hooks/use-agent-control-bar';
import useChatAndTranscription from '@/hooks/use-chat-and-transcription';
import { useDebugMode } from '@/hooks/useDebug';
import { useGlobalSilence } from '@/hooks/useGlobalSilent';
import { cn } from '@/lib/utils';
import { ChatEntrySkeleton } from '../chatSkelton/chatSkelton';
import { TrackToggle } from '../livekit/track-toggle';
import AnimatedOrb from './orbitVisualizer';
import WhiteBorder from './whiteBorder';
const TILE_TRANSITION = {
    easing: 'spring',
    stiffness: 675,
    damping: 75,
    mass: 1,
};
const TranscriptMotion = motion.create(Transcript);
export function useLocalTrackRef(source) {
    const { localParticipant } = useLocalParticipant();
    const publication = localParticipant.getTrackPublication(source);
    const trackRef = useMemo(() => (publication ? { source, participant: localParticipant, publication } : undefined), [source, publication, localParticipant]);
    return trackRef;
}
function isAgentAvailable(agentState) {
    return agentState == 'listening' || agentState == 'thinking' || agentState == 'speaking';
}
export const PopupView = ({ appConfig, disabled, sessionStarted, onEmbedError, ref, setPopupOpen, setAgentIsReady, setWidgetControll, loading, setShowInactivityModal, showInactivity, assetsBasePath = '', }) => {
    useDebugMode();
    const room = useRoomContext();
    const { state: agentState, audioTrack: agentAudioTrack, videoTrack: agentVideoTrack, } = useVoiceAssistant();
    const { isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();
    const [screenShareTrack] = useTracks([Track.Source.ScreenShare]);
    const cameraTrack = useLocalTrackRef(Track.Source.Camera);
    const [chatOpen, setChatOpen] = useState(false);
    const { messages, send } = useChatAndTranscription();
    const [flag, setFlagName] = useState('');
    const { mode, setMode } = useMode();
    const closeBtnRef = useRef(null);
    // const [isMuted, setIsMuted] = useState(false);
    const [muted, setMuted] = useState(false);
    const remoteParticipants = useRemoteParticipants();
    const { disableChatOnly, isChatOnly, enableChatOnly } = useChatOnly();
    const { isModalOpen, openModal, closeModal } = useModal();
    const { micPermission } = useMicPermission();
    const [micMute, setMicMute] = useState(false);
    // const { isSpeakerMuted, setIsSpeakerMuted } = useGlobalSilence(
    //   micPermission === 'denied' ? true : false
    // );
    const { isSpeakerMuted, setIsSpeakerMuted } = useGlobalSilence(micPermission === 'denied'
        ? true
        : typeof window !== 'undefined' && sessionStorage.getItem('chat') === 'true'
            ? true
            : false);
    React.useEffect(() => {
        if (typeof window !== 'undefined' && sessionStorage.getItem('chat') === 'true') {
            setChat(sessionStorage.getItem('chat') === 'true' ? true : false);
        }
    }, [sessionStorage]);
    const [isMuted, setIsMuted] = useState(() => {
        if (typeof window !== 'undefined') {
            return (sessionStorage.getItem('isSpeakerMuted') === 'true' &&
                sessionStorage.getItem('chat') === 'false');
        }
        return false;
    });
    // useEffect(() => {
    //   sessionStorage.setItem('isSpeakerMuted', String(isMuted));
    // }, [isMuted]);
    // useEffect(() => {
    //   setChat(
    //     flag === 'request_name' || flag === 'request_email' || flag === 'request_phone' ? true : false
    //   );
    // }, [flag]);
    // room diconnect when inactivity
    const timerRef = useRef(null);
    const INACTIVITY_TIMEOUT = 60000;
    useEffect(() => {
        // Always clear previous timer
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        // If modal is open, don't observe inactivity
        if (showInactivity) {
            return;
        }
        // Start inactivity timer when modal is closed
        timerRef.current = setTimeout(() => {
            setShowInactivityModal(true);
        }, INACTIVITY_TIMEOUT);
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [messages, showInactivity]);
    useEffect(() => {
        const onDataReceived = (payload, participant, kind, topic) => {
            try {
                const decoder = new TextDecoder();
                const message = decoder.decode(payload);
                const data = JSON.parse(message);
                let uiFlag = '';
                // Only set when backend actually sends ui_flag
                if (data && typeof data === 'object' && 'ui_flag' in data) {
                    uiFlag = data.ui_flag;
                }
                setFlagName(uiFlag);
                console.log('📨 Data Received from Backend:');
                console.log('  Topic:', topic);
                console.log('  From:', participant === null || participant === void 0 ? void 0 : participant.identity);
                console.log('  Data:', data);
                // setFlagName(data.ui_flag);
                // Specifically log UI flags like request_email
                if (data.type === 'ui_flag' || topic === 'ui_flag') {
                    console.log('🚩 UI Flag Received:', data);
                    console.log('  Flag Name:', data.flag || data.name);
                    console.log('  Payload:', data.payload || data.data || data.reason);
                }
            }
            catch (e) {
                console.log('⚠️ Could not parse data message:', e);
                console.log('Raw payload:', payload);
            }
        };
        room.on(RoomEvent.DataReceived, onDataReceived);
        return () => {
            setFlagName('');
            room.off(RoomEvent.DataReceived, onDataReceived);
        };
    }, [room]);
    const [chat, setChat] = useState(typeof window !== 'undefined' && sessionStorage.getItem('chat') === 'true' ? true : false);
    useEffect(() => {
        typeof window !== 'undefined' && sessionStorage.setItem('chat', chat ? 'true' : 'false');
    }, [chat]);
    // useEffect(() => {
    //   if (sessionStorage.getItem('chat') === 'true') {
    //     sessionStorage.setItem('speakerOn', 'false');
    //   }
    // }, []);
    useEffect(() => {
        if (sessionStorage.getItem('chat') === 'true') {
            // enableChatOnly();
            setChat(true);
            setChatOpen(true);
            setIsSpeakerMuted(true);
        }
        else {
            disableChatOnly();
            setChat(false);
            sessionStorage.getItem('isSpeakerMuted') === 'false' && setIsSpeakerMuted(false);
            setChatOpen(false);
        }
    }, []);
    const { supportsChatInput, supportsVideoInput, supportsScreenShare } = appConfig;
    const capabilities = {
        supportsChatInput,
        supportsVideoInput,
        supportsScreenShare,
    };
    async function onSendMessage(message) {
        await send(message);
        return;
    }
    // If the agent hasn't connected after an interval,
    // then show an error - something must not be working
    useEffect(() => {
        if (!sessionStarted) {
            return;
        }
        const timeout = setTimeout(() => {
            if (!isAgentAvailable(agentState)) {
                const reason = agentState === 'connecting'
                    ? 'Agent did not join the room. '
                    : 'Agent connected but did not complete initializing. ';
                onEmbedError({
                    title: 'Session ended',
                    description: _jsx("p", { className: "w-full", children: reason }),
                });
            }
        }, 10000);
        return () => clearTimeout(timeout);
    }, [agentState, sessionStarted, room, onEmbedError]);
    // useEffect(() => {
    //   setIsMuted(chat ? true : false);
    // }, [localStorage, chatOpen, chat]);
    // useEffect(() => {
    //   if (isMuted) {
    //     setIsSpeakerMuted(true);
    //   }
    // }, [isMuted]);
    React.useEffect(() => {
        if (sessionStorage.getItem('chat') === 'false' &&
            sessionStorage.getItem('isSpeakerMuted') === 'false') {
            setIsSpeakerMuted(false);
        }
        else if (sessionStorage.getItem('chat') === 'false' &&
            sessionStorage.getItem('speakerOn') === 'true') {
            setIsSpeakerMuted(true);
            setIsMuted(true);
        }
        else if (sessionStorage.getItem('chat') === 'false' &&
            sessionStorage.getItem('speakerOn') === 'false') {
            setIsSpeakerMuted(false);
            setIsMuted(false);
        }
        else if (!('speakerOn' in sessionStorage) && sessionStorage.getItem('chat') === 'true') {
            setIsSpeakerMuted(true);
            setIsMuted(true);
            // key does NOT exist
        }
        else if (!('speakerOn' in sessionStorage)) {
            setIsSpeakerMuted(false);
            setIsMuted(false);
            // key does NOT exist
        }
        else if (sessionStorage.getItem('chat') === 'false' &&
            sessionStorage.getItem('isSpeakerMuted') === 'true') {
            setIsSpeakerMuted(true);
            setIsMuted(true);
        }
        else if (sessionStorage.getItem('chat') === 'true' && chat) {
            setIsSpeakerMuted(true);
        }
    }, [chat]);
    // useEffect(() => {
    //   if (isChatOnly) {
    //     muteAllAudio(true);
    //     setIsMuted(true);
    //     console.log('i m entered in the loop');
    //   }
    // }, [isChatOnly]);
    useEffect(() => {
        if (sessionStorage.getItem('chat') === 'true' || isChatOnly) {
            sessionStorage.setItem('chat', 'true');
            setChat(true);
        }
        //  else if (flag === 'request_email' || flag === 'request_phone' || flag === 'request_name') {
        //   // sessionStorage.setItem('chat', 'true');
        // }
        else {
            sessionStorage.setItem('chat', 'false');
        }
    }, [flag]);
    useEffect(() => {
        if (flag === 'session_close') {
            const timer = setTimeout(() => {
                handleCloseAction();
            }, 10000); // 5 seconds
            // optional cleanup (in case flag changes before timeout finishes)
            return () => clearTimeout(timer);
        }
    }, [flag]);
    useEffect(() => {
        sessionStorage.setItem('agentClicked', 'false');
    }, []);
    // useEffect(() => {
    //   agentState === 'connecting'
    //     ? setAgentIsReady && setAgentIsReady(true)
    //     : setAgentIsReady && setAgentIsReady(false);
    // }, [agentState]);
    const [micRef, setMicRef] = useState(null);
    // const [muted, setMuted] = useState(false);
    // const streamRef: any = useRef(null);
    // const audioRef: any = useRef(null);
    // useEffect(() => {
    //   const setupMic = async () => {
    //     try {
    //       const stream: any = await navigator.mediaDevices.getUserMedia({ audio: true });
    //       streamRef.current = stream;
    //       // Create invisible audio element for playback
    //       const audio: any = document.createElement('audio');
    //       audio.srcObject = stream;
    //       audio.autoplay = true;
    //       audioRef.current = audio;
    //     } catch (err) {
    //       console.error('Mic access error:', err);
    //     }
    //   };
    //   setupMic();
    // }, []);
    // const toggleMute = () => {
    //   const newState = !muted;
    //   setMuted(newState);
    //   // Mute microphone
    //   if (streamRef.current) {
    //     streamRef.current.getAudioTracks().forEach((track: any) => {
    //       track.enabled = !newState; // Disable mic when muted
    //     });
    //   }
    //   // Mute speaker
    //   if (audioRef.current) {
    //     audioRef.current.muted = newState;
    //   }
    // };
    useEffect(() => {
        if (micPermission === 'granted' && sessionStorage.getItem('chat') === 'false') {
            typeof window !== 'undefined' && sessionStorage.setItem('chat', 'false');
            disableChatOnly();
        }
    }, [micPermission]);
    // first time user enter by clicking chat now
    useEffect(() => {
        if (micPermission === 'prompt' && sessionStorage.getItem('chat') === 'true') {
            sessionStorage.setItem('noPermission', 'true');
        }
    }, [micPermission]);
    // useEffect(() => {
    //   if (
    //     micPermission === 'granted' &&
    //     sessionStorage.getItem('noPermission') === 'true' &&
    //     sessionStorage.getItem('chat') === 'false'
    //   ) {
    //     disableChatOnly();
    //     setChat(false);
    //     setIsSpeakerMuted(false);
    //   }
    // }, [micPermission]);
    useEffect(() => {
        if (micPermission === 'granted' &&
            sessionStorage.getItem('noPermission') === 'true' &&
            sessionStorage.getItem('chat') === 'true') {
            disableChatOnly();
            setChat(false);
            setIsSpeakerMuted(false);
            setIsMuted(false);
        }
    }, [micPermission]);
    const [showConfirm, setShowConfirm] = useState(false);
    const handleCloseAction = () => {
        setPopupOpen && setPopupOpen(false);
        sessionStorage.setItem('hideWidget', 'true');
        setWidgetControll && setWidgetControll(true);
        disableChatOnly();
        sessionStorage.setItem('agentOpen', 'false');
        sessionStorage.setItem('micMute', 'false');
        sessionStorage.setItem('chat', 'false');
        sessionStorage.setItem('isSpeakerMuted', 'false');
        setMode('bottom-bar');
    };
    const saveUserChoices = true;
    const { micTrackRef, visibleControls, cameraToggle, microphoneToggle, screenShareToggle, handleAudioDeviceChange, handleVideoDeviceChange, } = useAgentControlBar({
        saveUserChoices,
    });
    return (_jsx(_Fragment, { children: _jsx("div", { ref: ref, inert: disabled, className: "flex h-full w-full flex-col overflow-hidden", children: _jsxs("div", { className: "flex h-full flex-col justify-between", children: [_jsxs("div", { className: "flex gap-2", children: [_jsxs(AnimatePresence, { children: [!agentVideoTrack && (_jsx(motion.div, { initial: {
                                            scale: 1,
                                            left: '50%',
                                            top: '50%',
                                            translateX: '-50%',
                                            translateY: '-50%',
                                            transformOrigin: 'center top',
                                        }, animate: {
                                            left: chatOpen ? '12px' : '12px',
                                            translateX: chatOpen ? '0' : '0',
                                            top: chatOpen ? 'auto' : 'auto',
                                            bottom: chatOpen ? '200px' : '200px',
                                            translateY: chatOpen ? '0' : '0',
                                            scale: chatOpen ? 0.275 : 0.275,
                                            transformOrigin: 'center bottom',
                                        }, transition: TILE_TRANSITION, className: cn('pointer-events-none absolute flex aspect-square w-95 items-center justify-start rounded-2xl border border-transparent bg-transparent transition-colors'), children: _jsx("div", { className: cn([
                                                'relative z-0 flex items-center justify-center',
                                                'h-95 w-95',
                                                ,
                                            ]), children: _jsx(AnimatedOrb, { isActive: agentState === 'speaking' }) }) }, "audio-visualizer")), _jsx("div", { className: "absolute bottom-0", children: _jsx(AudioVisualizer, { agentState: agentState, audioTrack: agentAudioTrack, setAgentIsReady: setAgentIsReady, chat: chat }) })] }), loading && sessionStorage.getItem('agentOpen') === 'true' ? (_jsx("div", { className: cn('scrollbar-on-hover flex max-h-[380px] grow flex-col overflow-x-hidden overflow-y-scroll py-3 pr-3 pl-1 md:ml-[200px]', '[mask-image:linear-gradient(0deg,rgba(0,0,0,0.2)_0%,rgba(0,0,0,1)_5%,rgba(0,0,0,1)_95%,rgba(0,0,0,0)_100%)]'), children: _jsxs("ul", { className: "space-y-4", children: [_jsx(ChatEntrySkeleton, {}), _jsx(ChatEntrySkeleton, { isUser: true }), _jsx(ChatEntrySkeleton, {})] }) })) : (_jsx(TranscriptMotion, { initial: {
                                    y: 10,
                                    opacity: 0,
                                }, animate: {
                                    y: chatOpen ? 0 : 10,
                                    opacity: chatOpen ? 1 : 1,
                                }, transition: {
                                    type: 'spring',
                                    duration: 0.5,
                                    bounce: 0,
                                }, messages: messages, agentState: agentState, audioTrack: micRef }))] }), !chatOpen && (_jsx("div", { className: "absolute right-20 bottom-10", children: _jsx(TrackToggle, { variant: "primary", source: Track.Source.Microphone, pressed: microphoneToggle.enabled, disabled: microphoneToggle.pending, onPressedChange: microphoneToggle.toggle, className: "peer/track group/track relative w-auto !bg-transparent pl-3 has-[+_*]:rounded-r-none has-[+_*]:border-r-0 has-[+_*]:pr-2", children: _jsx(BarVisualizer, { barCount: 5, trackRef: micTrackRef, options: { minHeight: 40 }, className: "absolute bottom-0 flex h-full w-auto items-center justify-center gap-1", children: _jsx("span", { className: cn([
                                        'gradient-bar h-full w-3 origin-center rounded-2xl',
                                        'group-data-[state=off]/track:bg-destructive-foreground group-data-[state=on]/track:bg-white',
                                        'data-lk-muted:bg-muted',
                                    ]) }) }) }) })), _jsxs("div", { className: "absolute top-[10px] right-[10px] flex flex-wrap items-center gap-4", children: [_jsx(WhiteBorder, { children: _jsxs("button", { onClick: () => {
                                        if (isChatOnly) {
                                            openModal();
                                            if (sessionStorage.getItem('chat') === 'true' && micPermission === 'granted') {
                                                setChat((prev) => !prev);
                                                setChatOpen(true);
                                            }
                                            if (!chatOpen) {
                                                setIsSpeakerMuted(true);
                                                setIsMuted(true);
                                            }
                                            else {
                                                if (micPermission === 'granted' &&
                                                    sessionStorage.getItem('isSpeakerMuted') === 'false') {
                                                    setIsSpeakerMuted(false);
                                                    setIsMuted(false);
                                                }
                                            }
                                        }
                                        else {
                                            if ((sessionStorage.getItem('chat') === 'true' && micPermission === 'prompt') ||
                                                micPermission === 'denied') {
                                                openModal();
                                            }
                                            else {
                                                setChat((prev) => !prev);
                                                setChatOpen(true);
                                            }
                                            if (!chatOpen && isMuted) {
                                                setIsSpeakerMuted(true);
                                                setIsMuted(true);
                                            }
                                            else {
                                                if (micPermission === 'granted' &&
                                                    sessionStorage.getItem('isSpeakerMuted') === 'false') {
                                                    setIsSpeakerMuted(false);
                                                }
                                                // setIsMuted(false);
                                            }
                                        }
                                    }, className: "group flex cursor-pointer items-center gap-3 rounded-full border-2 border-none bg-transparent py-3 pr-5 pl-5 text-lg font-medium text-white transition-all duration-300 hover:bg-transparent hover:text-gray-900", children: [_jsx("div", { className: "flex h-10 w-10 items-center justify-center rounded-full transition-colors group-hover:bg-orange-600", style: {
                                                backgroundImage: 'linear-gradient(145deg, #fb923c, #ea580c)',
                                                boxShadow: 'inset 2px 2px 4px rgba(255,255,255,0.4), inset -2px -2px 4px rgba(0,0,0,0.2), 2px 2px 5px rgba(0,0,0,0.3)',
                                            }, children: chatOpen ? (_jsx(Mic, { className: "h-6 w-6 text-white" })) : (_jsx("img", { src: getAssetUrl('assets/message.svg') })) }), _jsx("span", { className: "text-sm text-white", children: chatOpen ? 'Voice Chat' : 'Chat Now' })] }) }), !chatOpen && (_jsx(WhiteBorder, { children: _jsx("button", { onClick: () => {
                                        const nextState = !isMuted;
                                        setIsMuted(nextState);
                                        setIsSpeakerMuted(nextState);
                                        sessionStorage.setItem('speakerOn', nextState ? 'true' : 'false');
                                    }, className: "group flex cursor-pointer items-center gap-3 rounded-full bg-transparent py-3 pr-3 pl-3 text-lg font-medium text-white transition-all duration-300 hover:text-gray-900", children: _jsx("div", { className: "flex h-10 w-10 items-center justify-center rounded-full transition-colors", children: isMuted ? (
                                        // Unmute icon
                                        _jsx(SpeakerSlashIcon, { className: "h-6 w-6 text-[#FF0000] hover:text-[#FF0000]" })) : (
                                        // Mute (X) icon
                                        _jsx(SpeakerSimpleHighIcon, { className: "h-6 w-6 text-white hover:text-white" })) }) }) })), !chatOpen && (_jsx(WhiteBorder, { children: _jsx("button", { onClick: () => {
                                        setMicMute(!micMute);
                                        sessionStorage === null || sessionStorage === void 0 ? void 0 : sessionStorage.setItem('micMute', String(!micMute));
                                    }, className: "group flex cursor-pointer items-center gap-3 rounded-full bg-transparent py-3 pr-3 pl-3 text-lg font-medium text-white transition-all duration-300 hover:text-gray-900", children: _jsx("div", { className: "flex h-10 w-10 items-center justify-center rounded-full transition-colors", children: micMute ? (
                                        // Unmute icon
                                        _jsx(MicrophoneSlashIcon, { className: "h-6 w-6 text-[#FF0000] hover:text-[#FF0000]" })) : (
                                        // Mute (X) icon
                                        _jsx(MicrophoneIcon, { className: "h-6 w-6 text-white hover:text-white" })) }) }) })), _jsxs("div", { className: "relative", children: [_jsx(WhiteBorder, { children: _jsxs("button", { ref: closeBtnRef, className: "group flex cursor-pointer items-center gap-3 rounded-full border-2 border-none bg-transparent py-3 pr-5 pl-5 text-lg font-medium text-white transition-all duration-300 hover:text-gray-900", onClick: () => {
                                                setShowConfirm(true);
                                                // setChat(true);
                                            }, type: "button", children: [_jsx("div", { className: "flex h-10 w-10 items-center justify-center rounded-full transition-colors group-hover:bg-red-600", style: {
                                                        backgroundImage: 'linear-gradient(145deg, #ef4444, #b91c1c)',
                                                        boxShadow: 'inset 2px 2px 4px rgba(255,255,255,0.4), inset -2px -2px 4px rgba(0,0,0,0.2), 2px 2px 5px rgba(0,0,0,0.3)',
                                                    }, children: _jsx("svg", { className: "h-6 w-6 text-white", fill: "none", stroke: "currentColor", strokeWidth: "3", viewBox: "0 0 24 24", children: _jsx("path", { d: "M6 6L18 18M6 18L18 6" }) }) }), _jsx("span", { className: "text-sm text-white", children: "Close" })] }) }), showConfirm && closeBtnRef.current && (_jsxs("div", { className: !chatOpen
                                            ? 'xs:right-[10px] absolute right-[-50px] z-50 mt-1 rounded-2xl border border-white/20 px-5 py-4 shadow-xl'
                                            : 'absolute right-[10px] z-50 mt-1 rounded-2xl border border-white/20 px-5 py-4 shadow-xl', style: {
                                            transition: 'all 300ms ease',
                                            background: 'linear-gradient(135deg, rgba(74, 63, 112, 0.6), rgba(43, 38, 61, 0.5))',
                                        }, children: [_jsx("p", { className: "mb-3 text-sm text-white", children: "Are you sure you want to close?" }), _jsxs("div", { className: "flex justify-end gap-3", children: [_jsx("button", { className: "cursor-pointer rounded-lg bg-white/20 px-3 py-1.5 text-sm text-white hover:bg-white/30", onClick: () => {
                                                            setShowConfirm(false);
                                                            // setChat(false);
                                                        }, children: "Cancel" }), _jsx("button", { className: "cursor-pointer rounded-lg bg-red-500/80 px-3 py-1.5 text-sm text-white hover:bg-red-600/90", onClick: () => {
                                                            handleCloseAction();
                                                            setShowConfirm(false);
                                                            room.disconnect();
                                                            sessionStorage.setItem('hideWidget', 'true');
                                                            setMode('bottom-bar');
                                                            disableChatOnly();
                                                            sessionStorage.setItem('agentOpen', 'false');
                                                            sessionStorage.setItem('micMute', 'false');
                                                            sessionStorage.setItem('chat', 'false');
                                                        }, children: "Confirm" })] })] }))] })] }), _jsx("div", { className: "md:ml-[200px]", children: _jsx(motion.div, { initial: {
                                opacity: 0,
                                translateY: 8,
                            }, animate: {
                                opacity: sessionStarted ? 1 : 0,
                                translateY: sessionStarted ? 0 : 8,
                            }, transition: {
                                delay: 0.5,
                            }, children: _jsx(ActionBar, { capabilities: capabilities, onSendMessage: onSendMessage, onChatOpenChange: setChatOpen, setChat: setChat, chat: chat, setMic: setMicRef, flag: flag, setFlag: setFlagName, setClose: setShowConfirm, close: showConfirm, setMicOn: setMicMute, micOn: micMute, showInactivity: showInactivity }) }) })] }) }) }));
};
